import type { PublicConfig } from '@pa/api/public'
import { Clock, Mail, MapPin, Phone } from 'lucide-react'
import { hoursSummary } from '../model/hours'

export interface ContactBlockProps {
  contact: PublicConfig['contact']
  hours: PublicConfig['hours']
  /** Compact: phone and email only (P-04, P-05, P-06, P-14). Full: plus address and opening hours (P-02, P-17). */
  compact?: boolean
}

/** `tel:` keeps digits and a leading `+`. */
const telHref = (phone: string) => `tel:${phone.trim().startsWith('+') ? '+' : ''}${phone.replace(/\D/g, '')}`

/**
 * The contact block reused on dead ends (UserFlows §2.3), from C-10. Rows with no value are left out, so there is
 * never an empty or broken link (T-P02-04). Ported from the mockup's `patient/Frame.tsx` `ContactBlock`.
 */
export function ContactBlock({ contact, hours, compact = false }: ContactBlockProps) {
  const summary = hoursSummary(hours)
  return (
    <div className={`contact-block ${compact ? 'compact' : ''}`}>
      {contact.phone !== '' && (
        <a className="cb-row" href={telHref(contact.phone)}>
          <span className="cb-ic">
            <Phone aria-hidden />
          </span>
          <span>
            <small>Call us</small>
            <b>{contact.phone}</b>
          </span>
        </a>
      )}
      {contact.email !== '' && (
        <a className="cb-row" href={`mailto:${contact.email}`}>
          <span className="cb-ic">
            <Mail aria-hidden />
          </span>
          <span>
            <small>Email us</small>
            <b>{contact.email}</b>
          </span>
        </a>
      )}
      {!compact && contact.address !== '' && (
        <div className="cb-row">
          <span className="cb-ic">
            <MapPin aria-hidden />
          </span>
          <span>
            <small>Visit us</small>
            <b>{contact.address}</b>
          </span>
        </div>
      )}
      {!compact && (
        <div className="cb-row">
          <span className="cb-ic">
            <Clock aria-hidden />
          </span>
          <span>
            <small>Opening hours</small>
            {summary.map((line) => (
              <b key={line}>{line}</b>
            ))}
          </span>
        </div>
      )}
    </div>
  )
}
